import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import {
  apiRequest,
  getToken,
  convertToTime,
  devError,
} from "@ridecloud/shared";
import { useParams, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import {
  PlusCircle,
  MinusCircle,
  Car,
  MapPin,
  Clock,
  Calendar,
  Luggage,
  IndianRupee,
  Users,
  CheckCircle,
  Info,
  ArrowLeft,
  Loader2,
} from "lucide-react";

const RideForm = ({ mode = "create" }) => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const [pickupPoints, setPickupPoints] = useState([""]);
  const [dropoffPoints, setDropoffPoints] = useState([""]);
  const [pickupSuggestions, setPickupSuggestions] = useState([[]]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([[]]);
  const [cities, setCities] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [vehicle, setVehicle] = useState({
    name: "",
    seats: {
      front: { price: 0, total_seats: 0, available_seats: 0 },
      middle: { price: 0, total_seats: 0, available_seats: 0 },
      back: { price: 0, total_seats: 0, available_seats: 0 },
    },
  });
  const [luggage, setLuggage] = useState({ no_of_luggage: 0, sizes: [] });
  const [errors, setErrors] = useState({});

  const isEditMode = mode === "edit";

  // Fetch ride data on component mount for edit mode
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await apiRequest(async (APIv1) =>
          APIv1.get("/user/dashboard/get_cities", {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              "Content-Type": "application/json",
            },
          })
        );
        setCities(response.data);
      } catch (error) {
        devError("Error fetching cities:", error);
        toast.error("Failed to load cities data");
      }
    };

    const fetchRideData = async () => {
      if (isEditMode && rideId) {
        try {
          const response = await apiRequest(async (APIv1) =>
            APIv1.get(`/user/dashboard/ride/${rideId}`, {
              headers: {
                Authorization: `Bearer ${await getToken()}`,
                "Content-Type": "application/json",
              },
            })
          );
          const ride = response.data;
          setPickupPoints(ride.pickupPoints || [""]);
          setDropoffPoints(ride.dropoffPoints || [""]);
          setDate(ride.date || "");
          setTime(ride.time ? convertToTime(ride.time) : "");
          setVehicle(
            ride.vehicle || {
              name: "",
              seats: {
                front: { price: 0, total_seats: 0, available_seats: 0 },
                middle: { price: 0, total_seats: 0, available_seats: 0 },
                back: { price: 0, total_seats: 0, available_seats: 0 },
              },
            }
          );
          setLuggage(ride.luggage || { no_of_luggage: 0, sizes: [] });

          // Initialize suggestions arrays
          setPickupSuggestions(
            new Array(ride.pickupPoints?.length || 1).fill([])
          );
          setDropoffSuggestions(
            new Array(ride.dropoffPoints?.length || 1).fill([])
          );
        } catch (error) {
          devError("Error fetching ride data:", error);
          toast.error("Failed to fetch ride data");
        }
      } else {
        // Set default date and time for create mode
        const now = new Date();
        now.setHours(now.getHours() + 1);
        setDate(now.toISOString().split("T")[0]);
        setTime(now.toTimeString().split(":").slice(0, 2).join(":"));
      }
    };

    fetchCities();
    fetchRideData();
  }, [rideId, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    // Validate pickup points
    const invalidPickupPoints = pickupPoints.filter(
      (point) => point && !cities.includes(point)
    );
    if (invalidPickupPoints.length > 0) {
      newErrors.pickup = "Please select valid cities from the suggestions.";
    }

    // Validate dropoff points
    const invalidDropoffPoints = dropoffPoints.filter(
      (point) => point && !cities.includes(point)
    );
    if (invalidDropoffPoints.length > 0) {
      newErrors.dropoff = "Please select valid cities from the suggestions.";
    }

    // Validate vehicle name
    if (!vehicle.name.trim()) {
      newErrors.vehicleName = "Vehicle name is required.";
    }

    // Validate at least one seat section has seats
    const hasSeats = Object.values(vehicle.seats).some(
      (section) => section.total_seats > 0
    );
    if (!hasSeats) {
      newErrors.seats = "At least one seat section must have seats available.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const rideData = {
      pickupPoints: pickupPoints.filter((point) => point.trim()),
      dropoffPoints: dropoffPoints.filter((point) => point.trim()),
      date,
      time,
      vehicle,
      luggage,
      isAvailable: true,
    };

    try {
      let response;
      if (isEditMode) {
        response = await apiRequest(async (APIv1) =>
          APIv1.put(`/user/dashboard/update_ride/${rideId}`, rideData, {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              "Content-Type": "application/json",
            },
          })
        );
      } else {
        response = await apiRequest(async (APIv1) =>
          APIv1.post("/user/dashboard/post_ride", rideData, {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              "Content-Type": "application/json",
            },
          })
        );
      }

      toast.success(`Ride ${isEditMode ? "updated" : "posted"} successfully!`);

      setTimeout(() => {
        navigate("/my-rides", { replace: true });
      }, 2000);
    } catch (error) {
      devError(`Error ${isEditMode ? "updating" : "posting"} ride:`, error);
      toast.error(
        `Failed to ${isEditMode ? "update" : "post"} ride. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPoint = (type) => {
    if (type === "pickup") {
      setPickupPoints([...pickupPoints, ""]);
      setPickupSuggestions([...pickupSuggestions, []]);
    } else {
      setDropoffPoints([...dropoffPoints, ""]);
      setDropoffSuggestions([...dropoffSuggestions, []]);
    }
  };

  const handleRemovePoint = (type, index) => {
    if (type === "pickup") {
      if (pickupPoints.length > 1) {
        setPickupPoints(pickupPoints.filter((_, i) => i !== index));
        setPickupSuggestions(pickupSuggestions.filter((_, i) => i !== index));
      }
    } else {
      if (dropoffPoints.length > 1) {
        setDropoffPoints(dropoffPoints.filter((_, i) => i !== index));
        setDropoffSuggestions(dropoffSuggestions.filter((_, i) => i !== index));
      }
    }
  };

  const handleInputChange = (type, index, value) => {
    if (type === "pickup") {
      const newPoints = [...pickupPoints];
      newPoints[index] = value;
      setPickupPoints(newPoints);

      if (value) {
        const filteredSuggestions = cities
          .filter((city) => city.toLowerCase().startsWith(value.toLowerCase()))
          .slice(0, 10);

        const newSuggestions = [...pickupSuggestions];
        newSuggestions[index] = filteredSuggestions;
        setPickupSuggestions(newSuggestions);
      } else {
        const newSuggestions = [...pickupSuggestions];
        newSuggestions[index] = [];
        setPickupSuggestions(newSuggestions);
      }
    } else {
      const newPoints = [...dropoffPoints];
      newPoints[index] = value;
      setDropoffPoints(newPoints);

      if (value) {
        const filteredSuggestions = cities
          .filter((city) => city.toLowerCase().startsWith(value.toLowerCase()))
          .slice(0, 10);
        const newSuggestions = [...dropoffSuggestions];
        newSuggestions[index] = filteredSuggestions;
        setDropoffSuggestions(newSuggestions);
      } else {
        const newSuggestions = [...dropoffSuggestions];
        newSuggestions[index] = [];
        setDropoffSuggestions(newSuggestions);
      }
    }
  };

  const handleSuggestionClick = (type, index, city) => {
    if (type === "pickup") {
      const newPoints = [...pickupPoints];
      newPoints[index] = city;
      setPickupPoints(newPoints);
      const newSuggestions = [...pickupSuggestions];
      newSuggestions[index] = [];
      setPickupSuggestions(newSuggestions);
    } else {
      const newPoints = [...dropoffPoints];
      newPoints[index] = city;
      setDropoffPoints(newPoints);
      const newSuggestions = [...dropoffSuggestions];
      newSuggestions[index] = [];
      setDropoffSuggestions(newSuggestions);
    }
  };

  const updateSeatSection = (section, field, value) => {
    let updatedValue = value;

    if (field === "available_seats") {
      updatedValue = Math.min(
        Number(value),
        vehicle.seats[section].total_seats
      );
    }

    setVehicle((prev) => ({
      ...prev,
      seats: {
        ...prev.seats,
        [section]: {
          ...prev.seats[section],
          [field]: Number(updatedValue),
        },
      },
    }));
  };

  const handleNumberInput = (section, field, value) => {
    const numValue = Math.max(0, Math.floor(Number(value) || 0));
    updateSeatSection(section, field, numValue);

    if (field === "total_seats") {
      const currentAvailableSeats = vehicle.seats[section].available_seats;
      if (currentAvailableSeats > numValue) {
        updateSeatSection(section, "available_seats", numValue);
      }
    }
  };

  const handleLuggageChange = (value) => {
    const numValue = Math.max(0, Math.floor(Number(value) || 0));
    setLuggage((prev) => ({ ...prev, no_of_luggage: numValue }));
  };

  const getHeaderConfig = () => {
    if (isEditMode) {
      return {
        title: "Update Your Ride",
        subtitle: "Modify your journey details",
        backLink: "/my-rides",
      };
    }
    return {
      title: "Post a New Ride",
      subtitle: "Share your journey and connect with fellow travelers",
      backLink: "/dashboard",
    };
  };

  const headerConfig = getHeaderConfig();

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-blue-50 py-4 px-2 sm:px-4 lg:px-8"
      style={{
        paddingTop: Capacitor.isNativePlatform()
          ? "calc(env(safe-area-inset-top) + 1rem)"
          : undefined,
      }}
    >
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 sm:px-6 py-6 relative">
            <button
              onClick={() => navigate(headerConfig.backLink)}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                {headerConfig.title}
              </h1>
              <p className="text-emerald-100 mt-2 text-sm sm:text-base">
                {headerConfig.subtitle}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Pickup Points */}
          <div className="bg-white rounded-2xl shadow-lg overflow">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 sm:px-6 py-4">
              <h2 className="flex items-center gap-3 text-lg sm:text-xl font-semibold text-white">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                Pickup Points
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              {pickupPoints.map((point, index) => (
                <div key={index} className="mb-4 last:mb-0">
                  <div className="flex gap-2 sm:gap-3 relative">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) =>
                          handleInputChange("pickup", index, e.target.value)
                        }
                        placeholder={`Pickup point ${index + 1}`}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 transition-all duration-200 text-sm sm:text-base"
                        required
                      />
                      {pickupSuggestions[index] &&
                        pickupSuggestions[index].length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border-2 border-emerald-200 rounded-xl mt-1 shadow-lg z-10 max-h-60 overflow-y-auto">
                            {pickupSuggestions[index].map((city, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() =>
                                  handleSuggestionClick("pickup", index, city)
                                }
                                className="w-full text-left px-3 sm:px-4 py-2 sm:py-3 hover:bg-emerald-50 transition-colors duration-150 text-sm sm:text-base border-b border-emerald-100 last:border-b-0"
                              >
                                {city}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemovePoint("pickup", index)}
                      disabled={pickupPoints.length === 1}
                      className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 hover:bg-red-200 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-xl transition-all duration-200"
                    >
                      <MinusCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
              {errors.pickup && (
                <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  {errors.pickup}
                </p>
              )}
              <button
                type="button"
                onClick={() => handleAddPoint("pickup")}
                className="w-full mt-4 px-4 py-3 bg-emerald-50 hover:bg-emerald-100 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-700 font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <PlusCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Add Pickup Point
              </button>
            </div>
          </div>

          {/* Dropoff Points */}
          <div className="bg-white rounded-2xl shadow-lg overflow">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 sm:px-6 py-4">
              <h2 className="flex items-center gap-3 text-lg sm:text-xl font-semibold text-white">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                Dropoff Points
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              {dropoffPoints.map((point, index) => (
                <div key={index} className="mb-4 last:mb-0">
                  <div className="flex gap-2 sm:gap-3 relative">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) =>
                          handleInputChange("dropoff", index, e.target.value)
                        }
                        placeholder={`Dropoff point ${index + 1}`}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-red-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 text-sm sm:text-base"
                        required
                      />
                      {dropoffSuggestions[index] &&
                        dropoffSuggestions[index].length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border-2 border-red-200 rounded-xl mt-1 shadow-lg z-10 max-h-60 overflow-y-auto">
                            {dropoffSuggestions[index].map((city, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() =>
                                  handleSuggestionClick("dropoff", index, city)
                                }
                                className="w-full text-left px-3 sm:px-4 py-2 sm:py-3 hover:bg-red-50 transition-colors duration-150 text-sm sm:text-base border-b border-red-100 last:border-b-0"
                              >
                                {city}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemovePoint("dropoff", index)}
                      disabled={dropoffPoints.length === 1}
                      className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 hover:bg-red-200 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-xl transition-all duration-200"
                    >
                      <MinusCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
              {errors.dropoff && (
                <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  {errors.dropoff}
                </p>
              )}
              <button
                type="button"
                onClick={() => handleAddPoint("dropoff")}
                className="w-full mt-4 px-4 py-3 bg-red-50 hover:bg-red-100 border-2 border-dashed border-red-300 rounded-xl text-red-700 font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <PlusCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Add Dropoff Point
              </button>
            </div>
          </div>

          {/* Date and Time */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 sm:px-6 py-4">
              <h2 className="flex items-center gap-3 text-lg sm:text-xl font-semibold text-white">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
                Schedule
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    Date
                  </label>
                  <input
                    type="date"
                    value={date}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-sm sm:text-base"
                    required
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    Time
                  </label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-sm sm:text-base"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 sm:px-6 py-4">
              <h2 className="flex items-center gap-3 text-lg sm:text-xl font-semibold text-white">
                <Car className="w-5 h-5 sm:w-6 sm:h-6" />
                Vehicle Details
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700 mb-2">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  Vehicle Name
                </label>
                <input
                  type="text"
                  value={vehicle.name}
                  onChange={(e) => {
                    // Allow only alphabets, numbers, and spaces
                    let value = e.target.value.replace(/[^A-Za-z0-9 ]/g, "");

                    // Restrict to max 20 characters
                    if (value.length > 20) {
                      value = value.slice(0, 20);
                    }

                    setVehicle((prev) => ({ ...prev, name: value }));
                  }}
                  placeholder="e.g., Toyota Camry 2024, Honda Civic"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-200 text-sm sm:text-base"
                  required
                />

                {errors.vehicleName && (
                  <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    {errors.vehicleName}
                  </p>
                )}
              </div>

              {["front", "middle", "back"].map((section) => (
                <div
                  key={section}
                  className="mb-6 last:mb-0 bg-purple-50 rounded-xl p-4 sm:p-6"
                >
                  <h3 className="flex items-center gap-2 text-base sm:text-lg font-semibold text-purple-800 mb-4 capitalize">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                    {section} Seats
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <IndianRupee className="w-4 h-4 text-purple-600" />
                        Price per Seat
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={vehicle.seats[section].price}
                        onChange={(e) =>
                          handleNumberInput(section, "price", e.target.value)
                        }
                        className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200 text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        Total Seats
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={vehicle.seats[section].total_seats}
                        onChange={(e) =>
                          handleNumberInput(
                            section,
                            "total_seats",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200 text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        Available Seats
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={vehicle.seats[section].total_seats}
                        value={vehicle.seats[section].available_seats}
                        onChange={(e) =>
                          handleNumberInput(
                            section,
                            "available_seats",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200 text-sm"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {errors.seats && (
                <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  {errors.seats}
                </p>
              )}
            </div>
          </div>
          {/* Luggage Details */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 sm:px-6 py-4">
              <h2 className="flex items-center gap-3 text-lg sm:text-xl font-semibold text-white">
                <Luggage className="w-5 h-5 sm:w-6 sm:h-6" />
                Luggage Details
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700 mb-2">
                    <Luggage className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                    Number of Luggage
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={luggage.no_of_luggage}
                    onChange={(e) => handleLuggageChange(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all duration-200 text-sm sm:text-base"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700 mb-2">
                    <Luggage className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                    Allowed Sizes
                  </label>
                  <select
                    value={luggage.sizes[0] || ""}
                    onChange={(e) =>
                      setLuggage((prev) => ({
                        ...prev,
                        sizes: [e.target.value],
                      }))
                    }
                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-xl bg-white text-gray-700 text-sm sm:text-base
             focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-300 
             cursor-pointer shadow-sm hover:border-orange-400 hover:shadow-md"
                  >
                    <option value="" disabled className="text-gray-400">
                      🔽 Select size
                    </option>
                    <option value="Small">👜 Small</option>
                    <option value="Medium">🎒 Medium</option>
                    <option value="Large">🧳 Large</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pb-4">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 sm:py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl disabled:shadow-none transition-all duration-200 flex items-center justify-center gap-3 text-base sm:text-lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
                  Posting Ride...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                  Post Ride
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RideForm;
